<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Mobile Sidebar Overlay -->
    <div 
      v-if="sidebarOpen" 
      class="fixed inset-0 z-40 lg:hidden"
      @click="sidebarOpen = false"
    >
      <div class="absolute inset-0 bg-black bg-opacity-50"></div>
    </div>

    <!-- Sidebar -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col"
      :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Sidebar Header with Logo -->
      <div class="flex items-center justify-between h-20 px-6 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center">
          <div class="w-16 h-16 mr-4">
            <img 
              src="/logo6.png" 
              alt="Annzz Logo" 
              class="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 class="text-xl font-bold text-gray-900">ANNZZ</h1>
          </div>
        </div>
        
        <!-- Mobile Menu Button -->
        <button 
          @click="sidebarOpen = false"
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Close sidebar"
          title="Close sidebar"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- User Info Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <button
          @click="router.push('/customer/profile')"
          class="w-full flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-sm"
        >
          <UserDisplay />
        </button>
      </div>

      <!-- Contact Us Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <button
          @click="showContactModal = true"
          class="w-full flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 border border-gray-200 hover:border-orange-300 hover:shadow-sm"
        >
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          Contact Us
        </button>
      </div>

      <!-- Sidebar Navigation -->
      <nav class="flex flex-col flex-1 overflow-y-auto">
        <!-- Main Navigation Items -->
        <div class="flex-1 px-4 pt-8">
          <div class="space-y-2">
            <!-- Catalogue -->
            <router-link 
              to="/customer/catalogue" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/customer/catalogue' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Product Catalogue
            </router-link>
            
            <!-- Cart -->
            <router-link 
              to="/customer/cart" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/customer/cart' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
              </svg>
              Shopping Cart
            </router-link>
            
            <!-- My Orders (for customers) or Workshop Jobs (for workshop users) -->
            <router-link 
              v-if="userStore && userStore.isCustomer && !userStore.isWorkshop"
              to="/customer/my-orders" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/customer/my-orders' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              My Orders
            </router-link>
            
            <!-- Workshop Jobs (for workshop users) -->
            <router-link 
              v-if="userStore && userStore.isWorkshop"
              to="/workshop/jobs" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/workshop/jobs' 
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Workshop Jobs
            </router-link>
            
            <!-- Profile (for all authenticated users) -->
            <router-link 
              to="/customer/profile" 
              class="group flex items-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
              :class="[
                $route.path === '/customer/profile'
                  ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              ]"
              @click="sidebarOpen = false"
            >
              <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Profile
            </router-link>
          </div>
        </div>


        <!-- Bottom Section with Logout -->
        <div class="px-4 pb-6 flex-shrink-0">
          <!-- Logout Button -->
          <button 
            @click="handleLogout"
            class="w-full group flex items-center px-4 py-3 rounded-lg text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200"
          >
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </nav>
    </div>

    <!-- Main Content Area -->
    <div class="lg:pl-64">
      <!-- Mobile Menu Button -->
      <div class="lg:hidden fixed top-4 left-4 z-30">
        <button 
          @click="sidebarOpen = true"
          class="p-3 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-100 transition-all duration-200"
          aria-label="Open sidebar menu"
          title="Open sidebar menu"
        >
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      <!-- Page Content -->
      <div class="p-4 sm:p-6 lg:p-8 pt-20 lg:pt-4">
        <slot />
      </div>
    </div>

    <!-- Contact Us Modal -->
    <ContactUsModal 
      :show="showContactModal" 
      @close="showContactModal = false" 
    />
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import ContactUsModal from './ContactUsModal.vue'
import UserDisplay from './UserDisplay.vue'

export default {
  name: 'UserLayout',
  components: {
    UserDisplay,
    ContactUsModal
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userStore = useUserStore()
    const sidebarOpen = ref(false)
    const showContactModal = ref(false)

    const pageTitle = computed(() => {
      const path = route.path
      if (path === '/customer/catalogue') return 'Product Catalogue'
      if (path === '/customer/cart') return 'Shopping Cart'
      if (path === '/customer/my-orders') return 'My Orders'
      if (path === '/workshop/jobs') return 'Workshop Jobs'
      if (path === '/customer/profile') return 'My Profile'
      return 'ANNZZ'
    })

    const handleLogout = async () => {
      try {
        await userStore.signOut()
        router.push('/')
      } catch (error) {
        console.error('Logout failed:', error)
      }
    }

    return {
      sidebarOpen,
      showContactModal,
      pageTitle,
      handleLogout,
      userStore,
      router
    }
  }
}
</script> 